import type { Eu } from '../../acesso';
import { podeFazer } from '../../acesso';
import type { CasoDaFila, LocalDaConferencia, SeloDoCaso } from '../../auditoria';
import { Icone } from '../../design/Icone';
import type { NomeDoIcone } from '../../design/Icone';

/**
 * Peças comuns das Divergências 3.0 premium (dono, 13/10/2026): o selo do caso, os números com
 * sinal, a chave de cada ação (idempotência) e o contexto da fila que viaja entre as telas.
 */

/** O que a pessoa pode fazer aqui — sempre pela capacidade, nunca pelo nome. */
export function papeisDe(eu: Eu | null) {
  return {
    prateleira: podeFazer(eu, 'divergencia.conferir_prateleira'),
    estoque: podeFazer(eu, 'divergencia.conferir_estoque'),
    decide: podeFazer(eu, 'divergencia.decidir'),
    gestao: podeFazer(eu, 'giro.administrar') || podeFazer(eu, 'auditoria.ver_todos'),
    administra: podeFazer(eu, 'giro.administrar'),
  };
}

const SELO: Readonly<Record<SeloDoCaso, { rotulo: string; tom: string; icone: NomeDoIcone }>> = {
  URGENTE: { rotulo: 'Urgente', tom: 'critico', icone: 'warn' },
  REINCIDENTE: { rotulo: 'Reincidente', tom: 'atencao', icone: 'retry' },
  NAO_VERIFICADO: { rotulo: 'Não verificado', tom: 'atencao', icone: 'eye' },
  AGUARDANDO_PRATELEIRA: { rotulo: 'Aguardando prateleira', tom: 'neutro', icone: 'shelf' },
  AGUARDANDO_ESTOQUE: { rotulo: 'Aguardando estoque', tom: 'neutro', icone: 'box' },
  PRONTO_PARA_DECISAO: { rotulo: 'Pronto para decisão', tom: 'info', icone: 'gauge' },
  CONCLUIDO: { rotulo: 'Concluído', tom: 'ok', icone: 'check' },
};

export function Selo({ selo, titulo }: { selo: SeloDoCaso; titulo?: string | null }) {
  const s = SELO[selo];
  return (
    <span className={`dp-selo dp-selo-${s.tom}`} title={titulo ?? undefined}>
      <Icone nome={s.icone} tamanho={12} traco={2} />
      {s.rotulo}
    </span>
  );
}

/** O selo do caso, mesmo quando o servidor ainda não manda `prioridade` (versão antiga). */
export function seloDe(c: CasoDaFila): { selo: SeloDoCaso; nivel: number; motivo: string | null } {
  if (c.prioridade)
    return { selo: c.prioridade.selo, nivel: c.prioridade.nivel, motivo: c.prioridade.motivo };
  if (c.estado === 'CONCLUIDA') return { selo: 'CONCLUIDO', nivel: 3, motivo: null };
  if (c.atrasada) return { selo: 'URGENTE', nivel: 0, motivo: 'Prazo vencido' };
  if (c.etapa === 'AJUSTE') return { selo: 'PRONTO_PARA_DECISAO', nivel: 1, motivo: null };
  if (c.etapa === 'PRATELEIRA') return { selo: 'AGUARDANDO_PRATELEIRA', nivel: 2, motivo: null };
  return { selo: 'AGUARDANDO_ESTOQUE', nivel: 2, motivo: null };
}

/** "−6 un." / "+2 un." / "0 un." — o sinal tipográfico, não o hífen. */
/** Laboratório embaixo do nome — Prateleira, Estoque e Ajustar (dono, 15/10). */
export function Laboratorio({ nome }: { nome: string | null }) {
  const n = nome?.trim();
  return n ? (
    <div className="dp-lab">
      Laboratório <b>{n}</b>
    </div>
  ) : (
    <div className="dp-lab dp-lab-vazio">Laboratório não informado</div>
  );
}

export const comSinal = (n: number) =>
  `${n < 0 ? '−' : n > 0 ? '+' : ''}${String(Math.abs(n))} ${Math.abs(n) === 1 ? 'un.' : 'un.'}`;
export const unidades = (n: number) => `${String(n)} ${n === 1 ? 'unidade' : 'unidades'}`;

const HORA = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  hour: '2-digit',
  minute: '2-digit',
});
const DIA_HORA = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});
const hojeBR = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
const diaDe = (iso: string) =>
  new Date(iso).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
/** "14:05" hoje; "12/10 14:05" nos outros dias. Sempre horário de Brasília. */
export const quandoBR = (iso: string) =>
  diaDe(iso) === hojeBR() ? HORA.format(new Date(iso)) : DIA_HORA.format(new Date(iso));

/** UUID de UMA ação do usuário: o mesmo em cada nova tentativa do mesmo clique. */
export const novaChave = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.floor(Math.random() * 16);
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });

export const ROTULO_DO_LOCAL: Readonly<Record<LocalDaConferencia, string>> = {
  PRATELEIRA: 'Prateleira',
  ESTOQUE: 'Estoque',
};

/* ---------- contexto: a lista que a Central mostrou viaja para o caso ---------- */

const CHAVE_LISTA = 'dp:lista';
const CHAVE_CENTRAL = 'dp:central';

/** Guarda a ordem dos casos da página atual (para o "próximo" do caso). */
export function guardarLista(ids: readonly string[]) {
  try {
    sessionStorage.setItem(CHAVE_LISTA, JSON.stringify(ids));
  } catch {
    /* sem armazenamento: o caso só não tem "próximo" */
  }
}
export function lerLista(): string[] {
  try {
    const v = JSON.parse(sessionStorage.getItem(CHAVE_LISTA) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}
/** O último endereço da Central (filtros e aba) — voltar devolve o mesmo recorte. */
export function guardarCentral(endereco: string) {
  try {
    sessionStorage.setItem(CHAVE_CENTRAL, endereco);
  } catch {
    /* sem armazenamento */
  }
}
export function ultimaCentral(): string {
  try {
    return sessionStorage.getItem(CHAVE_CENTRAL) ?? '#/divergencias';
  } catch {
    return '#/divergencias';
  }
}
